import React, { useState, useMemo } from 'react';
import { Search, Filter, X, Star, Briefcase } from 'lucide-react';

const PortfolioList = ({ portfolios, isLoading, onEdit, onDelete, formatDate }) => {
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deletedIds, setDeletedIds] = useState(new Set());
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (status) {
      case 'published':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'draft':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'archived':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-blue-100 text-blue-800`;
    }
  };

  const getCategoryBadge = (category) => {
    if (!category) return null;
    return (
      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
        {category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </span>
    );
  };

  // Get unique categories and statuses for filter options
  const { categories, statuses } = useMemo(() => {
    const cats = new Set();
    const stats = new Set();
    
    portfolios.forEach(portfolio => {
      if (portfolio.category) cats.add(portfolio.category);
      stats.add(portfolio.status || 'published');
    });
    
    return {
      categories: Array.from(cats).sort(),
      statuses: Array.from(stats).sort()
    };
  }, [portfolios]);

  // Filter and search logic
  const filteredPortfolios = useMemo(() => {
    return portfolios.filter(portfolio => {
      const matchesSearch = !searchTerm || 
        portfolio.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        portfolio.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (portfolio.client_name && portfolio.client_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (portfolio.technologies && portfolio.technologies.some(tech => 
          tech.toLowerCase().includes(searchTerm.toLowerCase())
        ));
      
      const matchesStatus = statusFilter === 'all' || 
        (portfolio.status || 'published') === statusFilter;
      
      const matchesCategory = categoryFilter === 'all' || 
        portfolio.category === categoryFilter;
      
      const matchesFeatured = featuredFilter === 'all' || 
        (featuredFilter === 'featured' && portfolio.featured) ||
        (featuredFilter === 'not-featured' && !portfolio.featured);
      
      return matchesSearch && matchesStatus && matchesCategory && matchesFeatured;
    });
  }, [portfolios, searchTerm, statusFilter, categoryFilter, featuredFilter]);

  // Pagination logic
  const totalPages = Math.ceil(filteredPortfolios.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredPortfolios.slice(startIndex, endIndex);

  // Reset to first page when filters change
  const resetPagination = () => {
    setCurrentPage(1);
  };

  // Update pagination when filters change
  React.useEffect(() => {
    resetPagination();
  }, [searchTerm, statusFilter, categoryFilter, featuredFilter]);

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCategoryFilter('all');
    setFeaturedFilter('all');
    resetPagination();
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || categoryFilter !== 'all' || featuredFilter !== 'all';

  // Pagination controls
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  // Generate pagination numbers
  const getPaginationNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const handleDeleteClick = (portfolio) => {
    setDeleteConfirm(portfolio);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm) {
      setDeletingId(deleteConfirm.id);
      
      try {
        await onDelete(deleteConfirm.id);
        
        // Add to deleted set for fade effect
        setDeletedIds(prev => new Set([...prev, deleteConfirm.id]));
        
        // Clean up after animation
        setTimeout(() => {
          setDeletedIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(deleteConfirm.id);
            return newSet;
          });
        }, 500);
        
      } catch (error) {
        console.error('Delete failed:', error);
      } finally {
        setDeletingId(null);
        setDeleteConfirm(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirm(null);
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex space-x-4">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/6"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filter Bar */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by title, client, technologies..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>

            {/* Filter Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium transition-colors ${
                  showFilters 
                    ? 'bg-blue-50 text-blue-700 border-blue-300' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                    {[searchTerm, statusFilter !== 'all', categoryFilter !== 'all', featuredFilter !== 'all'].filter(Boolean).length}
                  </span>
                )}
              </button>
              
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-gray-600 hover:text-gray-800 underline"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="all">All Statuses</option>
                  {statuses.map(status => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="all">All Categories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              {/* Featured Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Featured
                </label>
                <select
                  value={featuredFilter}
                  onChange={(e) => setFeaturedFilter(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="all">All Items</option>
                  <option value="featured">Featured Only</option>
                  <option value="not-featured">Not Featured</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Count and Items Per Page */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mx-2">
        <div className="flex items-center gap-4">
          <p className="text-sm text-gray-600">
            {filteredPortfolios.length === portfolios.length 
              ? `${portfolios.length} portfolio item${portfolios.length !== 1 ? 's' : ''}`
              : `${filteredPortfolios.length} of ${portfolios.length} portfolio item${portfolios.length !== 1 ? 's' : ''}`
            }
            {filteredPortfolios.length > 0 && (
              <span className="text-gray-400 ml-2">
                (showing {startIndex + 1}-{Math.min(endIndex, filteredPortfolios.length)})
              </span>
            )}
          </p>
          
          {filteredPortfolios.length > 10 && (
            <div className="flex items-center gap-2">
              <label htmlFor="itemsPerPage" className="text-sm text-gray-600">
                Show:
              </label>
              <select
                id="itemsPerPage"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-600">per page</span>
            </div>
          )}
        </div>
        
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2">
            {searchTerm && (
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                Search: "{searchTerm}"
                <button
                  onClick={() => setSearchTerm('')}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {statusFilter !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                Status: {statusFilter}
                <button
                  onClick={() => setStatusFilter('all')}
                  className="ml-1 text-green-600 hover:text-green-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {categoryFilter !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                Category: {categoryFilter.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                <button
                  onClick={() => setCategoryFilter('all')}
                  className="ml-1 text-purple-600 hover:text-purple-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {featuredFilter !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                {featuredFilter === 'featured' ? 'Featured Only' : 'Not Featured'}
                <button
                  onClick={() => setFeaturedFilter('all')}
                  className="ml-1 text-yellow-600 hover:text-yellow-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* No Results */}
      {currentItems.length === 0 && filteredPortfolios.length === 0 && portfolios.length > 0 && (
        <div className="p-8 text-center">
          <Search className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No portfolio items found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Try adjusting your search terms or filters to find what you're looking for.
          </p>
          <button
            onClick={clearAllFilters}
            className="mt-3 text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Empty State */}
      {portfolios.length === 0 && (
        <div className="p-8 text-center">
          <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No portfolio items</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating your first portfolio item.</p>
        </div>
      )}

      {/* Portfolio Items List */}
      {currentItems.length > 0 && (
        <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
          {/* Mobile view */}
          <div className="sm:hidden">
            {currentItems.map((portfolio) => (
              <div 
                key={portfolio.id} 
                className={`border-b border-gray-200 p-4 transition-all duration-500 ${
                  deletedIds.has(portfolio.id) 
                    ? 'opacity-0 transform -translate-x-full' 
                    : 'opacity-100 transform translate-x-0'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1 min-w-0 pr-4">
                    <h3 className="text-sm font-medium text-gray-900 truncate flex items-center">
                      {portfolio.featured && <Star className="w-4 h-4 text-yellow-500 mr-1 flex-shrink-0" />}
                      {portfolio.title}
                    </h3>
                    <p className="text-xs text-gray-500 truncate">{portfolio.slug}</p>
                  </div>
                  <div className="flex space-x-1 flex-shrink-0">
                    <button
                      onClick={() => onEdit(portfolio)}
                      className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClick(portfolio)}
                      disabled={deletingId === portfolio.id}
                      className="text-red-600 hover:text-red-800 text-xs px-2 py-1 disabled:opacity-50"
                    >
                      {deletingId === portfolio.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-2">
                  {portfolio.category && getCategoryBadge(portfolio.category)}
                  <span className={getStatusBadge(portfolio.status || 'published')}>
                    {portfolio.status || 'published'}
                  </span>
                </div>
                
                {portfolio.client_name && (
                  <p className="text-xs text-gray-600 mb-1">Client: {portfolio.client_name}</p>
                )}
                
                {portfolio.technologies && portfolio.technologies.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {portfolio.technologies.slice(0, 3).map((tech, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                      >
                        {tech}
                      </span>
                    ))}
                    {portfolio.technologies.length > 3 && (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                        +{portfolio.technologies.length - 3}
                      </span>
                    )}
                  </div>
                )}
                
                <p className="text-xs text-gray-500">
                  Created: {formatDate(portfolio.created_at)}
                </p>
              </div>
            ))}
          </div>

          {/* Desktop view */}
          <div className="hidden sm:block">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-0">
                      Project
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Status
                    </th>
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Technologies
                    </th> */}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentItems.map((portfolio) => (
                    <tr 
                      key={portfolio.id} 
                      className={`hover:bg-gray-50 transition-all duration-500 ${
                        deletedIds.has(portfolio.id) 
                          ? 'opacity-0 transform -translate-x-full' 
                          : 'opacity-100 transform translate-x-0'
                      }`}
                    >
                      <td className="px-6 py-4 max-w-xs">
                        <div>
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-gray-900 truncate flex items-center">
                              {portfolio.featured && (
                                <Star className="w-4 h-4 text-yellow-500 mr-2 flex-shrink-0" />
                              )}
                              {portfolio.title}
                            </div>
                          </div>
                          <div className="text-sm text-gray-500 truncate">{portfolio.slug}</div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        {portfolio.category ? getCategoryBadge(portfolio.category) : '-'}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs">
                        <div className="truncate">{portfolio.client_name || '-'}</div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(portfolio.status || 'published')}>
                          {portfolio.status || 'published'}
                        </span>
                      </td>
                      
                      {/* <td className="px-6 py-4 max-w-xs">
                        <div className="flex flex-wrap gap-1">
                          {(portfolio.technologies || []).slice(0, 3).map((tech, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                            >
                              {tech}
                            </span>
                          ))}
                          {(portfolio.technologies || []).length > 3 && (
                            <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                              +{(portfolio.technologies || []).length - 3}
                            </span>
                          )}
                        </div>
                      </td> */}
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(portfolio.created_at)}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium w-32">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => onEdit(portfolio)}
                            className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(portfolio)}
                            disabled={deletingId === portfolio.id}
                            className="text-red-600 hover:text-red-900 transition-colors duration-200 disabled:opacity-50"
                          >
                            {deletingId === portfolio.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {filteredPortfolios.length > itemsPerPage && (
        <div className="bg-white px-4 py-3 border border-gray-200 rounded-lg shadow-sm">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Page info */}
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
              <span className="font-medium">{Math.min(endIndex, filteredPortfolios.length)}</span> of{' '}
              <span className="font-medium">{filteredPortfolios.length}</span> results
            </div>

            {/* Pagination controls */}
            <div className="flex items-center gap-1">
              {/* First page */}
              <button
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                className="px-2 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First page"
              >
                ««
              </button>

              {/* Previous page */}
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="px-2 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous page"
              >
                ‹
              </button>

              {/* Page numbers */}
              <div className="flex">
                {getPaginationNumbers().map((number, index) => (
                  <button
                    key={index}
                    onClick={() => typeof number === 'number' && goToPage(number)}
                    disabled={typeof number !== 'number'}
                    className={`px-3 py-1 text-sm font-medium border border-gray-300 ${
                      number === currentPage
                        ? 'bg-blue-50 text-blue-600 border-blue-500 z-10'
                        : typeof number === 'number'
                        ? 'bg-white text-gray-700 hover:bg-gray-50'
                        : 'bg-white text-gray-400 cursor-default'
                    } ${
                      index === 0 ? '-ml-px' : '-ml-px'
                    }`}
                  >
                    {number}
                  </button>
                ))}
              </div>

              {/* Next page */}
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed -ml-px"
                title="Next page"
              >
                ›
              </button>

              {/* Last page */}
              <button
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed -ml-px"
                title="Last page"
              >
                »»
              </button>
            </div>

            {/* Quick page jump (on larger screens) */}
            <div className="hidden md:flex items-center gap-2">
              <span className="text-sm text-gray-700">Go to page:</span>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value);
                  if (page >= 1 && page <= totalPages) {
                    goToPage(page);
                  }
                }}
                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="text-sm text-gray-700">of {totalPages}</span>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full animate-in fade-in slide-in-from-bottom-4 duration-200">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Delete Portfolio Item
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Are you sure you want to delete <span className="font-medium text-gray-900">"{deleteConfirm.title}"</span>? 
                  This action cannot be undone.
                </p>
                <div className="flex space-x-3 justify-center">
                  <button
                    onClick={handleCancelDelete}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={deletingId === deleteConfirm.id}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deletingId === deleteConfirm.id ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Deleting...
                      </span>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioList;